/* Dedicated views for the 11 Mutation Breeding analyses.
   Charts use Recharts. Most views delegate to lightweight table
   renderers here (to avoid touching GenericAnalysisView).
*/
import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ReferenceLine, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';

const AMBER = '#B45309';
const AMBER_ACTIVE = '#D97706';

const fmt = (v, n = 4) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') return v.toFixed(n);
  return String(v);
};

function Citations() {
  return (
    <div className="rounded-xl p-4 mt-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }} data-testid="mutation-citations">
      <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: '#92400E' }}>Materials & Methods — References</div>
      <ul className="text-xs space-y-0.5" style={{ color: '#78350F' }}>
        <li>Gaul, H. (1964). <i>Mutations in Plant Breeding.</i> Radiation Botany.</li>
        <li>FAO/IAEA (1977). <i>Manual on Mutation Breeding</i>, 2nd Ed.</li>
        <li>Konzak, C.F. et al. (1965). Efficient chemical mutagenesis. IAEA Tech. Rep. 47.</li>
        <li>Kharkwal, M.C. (2012). <i>ICAR Handbook on Mutation Breeding in India.</i></li>
        <li>PPV&amp;FR Authority (2007). DUS Guidelines for Mutant Varieties.</li>
      </ul>
    </div>
  );
}

function Card({ title, children, color = AMBER, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white border border-amber-200" data-testid={testid}>
      <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, cols }) {
  if (!rows || !rows.length) return <p className="text-xs text-gray-500">No data.</p>;
  const keys = cols || Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr className="bg-amber-100 text-amber-900">
          {keys.map(k => <th key={k} className="px-2 py-1.5 border border-amber-200 text-left">{String(k).replace(/_/g, ' ')}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50'}>
              {keys.map(k => {
                const v = r[k];
                const s = typeof v === 'number' ? fmt(v, k.toLowerCase().includes('p_') ? 6 : 4) : String(v ?? '-');
                return <td key={k} className="px-2 py-1 border border-amber-200 font-mono">{s}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* 1. DOSE RESPONSE */
export function DoseResponseView({ data }) {
  const [trait, setTrait] = useState(data?.traits_analyzed?.[0]);
  if (!data) return null;
  const t = data.per_trait?.[trait];
  return (
    <div className="space-y-4" data-testid="mutation-dose-response-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['Doses', data.n_doses], ['Reps', data.n_reps ?? '—'], ['Traits', data.traits_analyzed.length], ['Levels', data.dose_levels.join(', ')]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-amber-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-sm font-bold" style={{ color: '#78350F' }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        {data.traits_analyzed.map(tr => (
          <button key={tr} onClick={() => setTrait(tr)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
            style={{ background: trait === tr ? AMBER_ACTIVE : 'white', color: trait === tr ? 'white' : '#374151', border: '1px solid #FCD34D' }}
            data-testid={`dr-trait-${tr}`}>
            {tr}
          </button>
        ))}
      </div>
      {t && (
        <>
          <Card title="ANOVA" testid="dr-anova">
            <Table rows={t.anova} />
          </Card>
          <Card title="Linear & Quadratic Regression" testid="dr-regression">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg p-3 bg-amber-50">
                <div className="text-xs font-bold" style={{ color: AMBER }}>Linear</div>
                <p className="font-mono text-xs mt-1">{t.linear_regression.equation}</p>
                <div className="text-xs mt-1">R² = <b>{fmt(t.linear_regression.R2)}</b> · p = <b>{fmt(t.linear_regression.p_value, 6)}</b></div>
              </div>
              <div className="rounded-lg p-3 bg-amber-50">
                <div className="text-xs font-bold" style={{ color: AMBER }}>Quadratic</div>
                <p className="font-mono text-xs mt-1">{t.quadratic_regression.equation}</p>
                <div className="text-xs mt-1">R² = <b>{fmt(t.quadratic_regression.R2_quadratic)}</b></div>
              </div>
            </div>
            {t.optimal_dose != null && (
              <div className="mt-3 rounded-lg p-3 text-sm" style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}>
                <b>Optimal dose</b> (quadratic turning point): <span className="font-mono font-bold">{t.optimal_dose}</span>
              </div>
            )}
          </Card>
          <Card title="Dose-Response Curve" testid="dr-chart">
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={t.curve_data}>
                  <CartesianGrid stroke="#FEF3C7" />
                  <XAxis dataKey="dose" tick={{ fontSize: 11 }} label={{ value: 'Dose', position: 'insideBottom', offset: -3 }} />
                  <YAxis tick={{ fontSize: 11 }} label={{ value: trait, angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="observed" stroke="#1F2937" strokeWidth={0} dot={{ r: 4, fill: AMBER_ACTIVE }} name="Observed" />
                  <Line type="monotone" dataKey="linear" stroke="#2563EB" dot={false} name="Linear fit" />
                  <Line type="monotone" dataKey="quadratic" stroke={AMBER_ACTIVE} strokeWidth={2} dot={false} name="Quadratic fit" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
      <Citations />
    </div>
  );
}

/* 2. LD50 / GR50 */
export function LD50GR50View({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-ld50-gr50-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['LD50 (probit)', data.summary.LD50_probit], ['GR50 (probit)', data.summary.GR50_probit],
          ['LD50 (interp.)', data.summary.LD50_interpolation], ['GR50 (interp.)', data.summary.GR50_interpolation]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-amber-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-lg font-bold" style={{ color: AMBER }}>{fmt(v, 3)}</div>
          </div>
        ))}
      </div>
      {Object.entries(data.per_trait || {}).map(([label, t]) => (
        <Card key={label} title={label} testid={`ld50-trait-${label}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <Table rows={t.dose_response} />
            </div>
            <div className="rounded-lg p-3 bg-amber-50">
              <div className="font-bold text-sm" style={{ color: AMBER }}>Probit regression</div>
              {t.probit_regression ? (
                <ul className="text-xs mt-1 space-y-0.5 font-mono">
                  <li>Slope: <b>{fmt(t.probit_regression.slope)}</b></li>
                  <li>Intercept: <b>{fmt(t.probit_regression.intercept)}</b></li>
                  <li>R²: <b>{fmt(t.probit_regression.R2)}</b></li>
                  <li>p-value: <b>{fmt(t.probit_regression.p_value, 6)}</b></li>
                  <li>n points: {t.probit_regression.n_points}</li>
                </ul>
              ) : <p className="text-xs">Insufficient points for probit.</p>}
              <div className="mt-2 text-xs">
                <b>50% endpoint:</b>
                <div>Probit: <span className="font-mono font-bold">{fmt(t.probit_50, 3)}</span></div>
                <div>Interpolation: <span className="font-mono font-bold">{fmt(t.linear_interpolation_50, 3)}</span></div>
                {t.ci_95 && <div className="text-[11px] text-gray-600 mt-1">95% CI: {fmt(t.ci_95.lower_95, 3)} – {fmt(t.ci_95.upper_95, 3)}</div>}
              </div>
            </div>
          </div>
          {/* Chart */}
          <div style={{ width: '100%', height: 280 }} className="mt-3">
            <ResponsiveContainer>
              <LineChart data={t.dose_response}>
                <CartesianGrid stroke="#FEF3C7" />
                <XAxis dataKey="Dose" tick={{ fontSize: 11 }} label={{ value: 'Dose', position: 'insideBottom', offset: -3 }} />
                <YAxis tick={{ fontSize: 11 }} label={{ value: '% of control', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Line type="monotone" dataKey="Relative_percent" stroke={AMBER_ACTIVE} strokeWidth={2} name="% of control" />
                {t.probit_50 != null && <ReferenceLine x={t.probit_50} stroke="#DC2626" strokeDasharray="4 2" label={{ value: `50% (${fmt(t.probit_50, 2)})`, fontSize: 10, fill: '#DC2626' }} />}
                <ReferenceLine y={50} stroke="#6B7280" strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ))}
      <Citations />
    </div>
  );
}

/* 3. MUTATION FREQUENCY */
export function MutationFrequencyView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-frequency-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="grid grid-cols-3 gap-2">
        {[['Control µ', data.control_frequency], ['Total mutants', data.total_mutants], ['Plants screened', data.total_plants_screened]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-amber-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold" style={{ color: AMBER }}>{typeof v === 'number' ? fmt(v, 6) : v}</div>
          </div>
        ))}
      </div>
      <Card title="Mutation Frequency per Treatment" testid="mf-table">
        <Table rows={data.frequency_table} cols={['Treatment', 'Mutants', 'Total_plants', 'Frequency_percent', 'Frequency_per_1000', 'CI_95_lower', 'CI_95_upper', 'Frequency_above_control']} />
      </Card>
      <Card title="Frequency per 1000 Plants" testid="mf-chart">
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.frequency_table}>
              <CartesianGrid stroke="#FEF3C7" />
              <XAxis dataKey="Treatment" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="Frequency_per_1000" fill={AMBER_ACTIVE} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Citations />
    </div>
  );
}

/* 4. SPECTRUM */
export function MutantSpectrumView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-spectrum-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <Card title="Spectrum (counts & %)" testid="sp-table"><Table rows={data.spectrum_table} /></Card>
      {data.chi_square_tests?.length > 0 && (
        <Card title="Chi-square vs Uniform Expected" testid="sp-chi"><Table rows={data.chi_square_tests} /></Card>
      )}
      <Card title="Distribution Chart" testid="sp-chart">
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={data.spectrum_table}>
              <CartesianGrid stroke="#FEF3C7" />
              <XAxis dataKey="Treatment" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Legend />
              {data.mutant_types.map((t, i) => (
                <Bar key={t} dataKey={t} stackId="a" fill={['#B45309', '#D97706', '#F59E0B', '#FCD34D', '#FDE68A', '#FEF3C7'][i % 6]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Citations />
    </div>
  );
}

/* 5. M2 GENERATION */
export function M2GenerationView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-m2-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['Families', data.n_families], ['Segregating', data.n_segregating_families],
          ['Segregation %', `${fmt(data.segregation_percent, 2)}%`], ['Overall mean', fmt(data.overall_mean)]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-amber-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-sm font-bold" style={{ color: AMBER }}>{v}</div>
          </div>
        ))}
      </div>
      <Card title="Top Mutant Candidates (largest shift from overall mean)" testid="m2-top"><Table rows={data.top_mutant_candidates} /></Card>
      <Card title="All Family Statistics" testid="m2-all"><Table rows={data.family_statistics} /></Card>
      <Citations />
    </div>
  );
}

/* 6. CHLOROPHYLL */
export function ChlorophyllMutationView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-chlorophyll-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="rounded-lg p-3 bg-white border border-amber-200 inline-block">
        <div className="text-[10px] uppercase text-gray-500">Overall chlorophyll-mutation frequency</div>
        <div className="font-mono text-xl font-bold" style={{ color: AMBER }}>{fmt(data.overall_frequency_percent, 3)}%</div>
      </div>
      <Card title="Per-treatment Chlorophyll Mutation Table" testid="chl-table"><Table rows={data.per_treatment} /></Card>
      <Card title="Frequency Bar Chart" testid="chl-chart">
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.per_treatment}>
              <CartesianGrid stroke="#FEF3C7" />
              <XAxis dataKey="Treatment" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="Frequency_percent" fill={AMBER_ACTIVE} name="Total chlorophyll mutants %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Citations />
    </div>
  );
}

/* 7. QUANTITATIVE */
export function QuantitativeMutationView({ data }) {
  const [trait, setTrait] = useState(data?.traits_analyzed?.[0]);
  if (!data) return null;
  const t = data.per_trait?.[trait];
  return (
    <div className="space-y-4" data-testid="mutation-quantitative-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="rounded-lg p-3 bg-white border border-amber-200 inline-block">
        <div className="text-[10px] uppercase text-gray-500">Control group</div>
        <div className="font-mono text-base font-bold">{data.control_group}</div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {data.traits_analyzed.map(tr => (
          <button key={tr} onClick={() => setTrait(tr)} className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
            style={{ background: trait === tr ? AMBER_ACTIVE : 'white', color: trait === tr ? 'white' : '#374151', border: '1px solid #FCD34D' }}>
            {tr}
          </button>
        ))}
      </div>
      {t && <Card title={`${trait} — comparisons vs control`} testid="qm-table"><Table rows={t.comparisons} /></Card>}
      <Citations />
    </div>
  );
}

/* 8. RATE */
export function MutationRateView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-rate-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="rounded-lg p-3 bg-white border border-amber-200 inline-block">
        <div className="text-[10px] uppercase text-gray-500">Average rate per 1000 plants</div>
        <div className="font-mono text-xl font-bold" style={{ color: AMBER }}>{fmt(data.average_rate_per_1000, 4)}</div>
      </div>
      <Card title="Mutation Rate Table" testid="rate-table"><Table rows={data.mutation_rates} /></Card>
      <Citations />
    </div>
  );
}

/* 9. COMPARATIVE MUTAGEN */
export function ComparativeMutagenView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-comparative-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.best_effectiveness && (
          <div className="rounded-xl p-3 bg-amber-50 border border-amber-300">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: AMBER }}>Best effectiveness</div>
            <div className="text-sm font-bold mt-1">{data.best_effectiveness.mutagen} @ {data.best_effectiveness.dose}</div>
            <div className="text-xs font-mono text-gray-600">{data.best_effectiveness.effectiveness} mutants / unit dose</div>
          </div>
        )}
        {data.best_efficiency && (
          <div className="rounded-xl p-3 bg-amber-50 border border-amber-300">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: AMBER }}>Best efficiency</div>
            <div className="text-sm font-bold mt-1">{data.best_efficiency.mutagen} @ {data.best_efficiency.dose}</div>
            <div className="text-xs font-mono text-gray-600">Efficiency: {data.best_efficiency.efficiency}</div>
          </div>
        )}
      </div>
      <Card title="Comparison Table" testid="comp-table"><Table rows={data.comparison_table} /></Card>
      <Citations />
    </div>
  );
}

/* 10. M3 PROGENY */
export function M3ProgenyView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="mutation-m3-view">
      <div className="rounded-xl p-3 bg-amber-50 border border-amber-300 text-sm">
        <b>Recommendation:</b> {data.recommendation}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[['Families tested', data.n_families_tested], ['True-breeding', data.n_true_breeding],
          ['3:1 segregating', data.n_3to1_segregating], ['15:1 segregating', data.n_15to1_segregating]].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-amber-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold" style={{ color: AMBER }}>{v}</div>
          </div>
        ))}
      </div>
      <Card title="Per-family Segregation Test" testid="m3-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-amber-100 text-amber-900">
              {['Family', 'n', 'Normal', 'Mutant', 'Mean', 'CV%', 'Shift%', 'χ²(3:1)', 'p(3:1)', 'χ²(15:1)', 'p(15:1)', 'True-breeding', 'Pattern'].map(h => (
                <th key={h} className="px-2 py-1.5 border border-amber-200 text-left text-[11px]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.families.map((f, i) => (
                <tr key={i} className={f.True_breeding ? 'bg-emerald-50' : (i % 2 === 0 ? 'bg-white' : 'bg-amber-50')}>
                  <td className="px-2 py-1 border border-amber-200 font-semibold">{f.Family}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{f.n_plants}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{f.Normal}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{f.Mutant_type}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.Mean)}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.CV_percent, 2)}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.Shift_percent, 2)}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.chi2_3to1)}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.p_3to1, 6)}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.chi2_15to1)}</td>
                  <td className="px-2 py-1 border border-amber-200 font-mono">{fmt(f.p_15to1, 6)}</td>
                  <td className="px-2 py-1 border border-amber-200 text-center">
                    {f.True_breeding ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">YES</span>
                      : <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-300 text-gray-700">no</span>}
                  </td>
                  <td className="px-2 py-1 border border-amber-200 text-[11px]">{f.Segregation_pattern}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Citations />
    </div>
  );
}

/* 11. SCREENING INDEX */
export function MutantScreeningIndexView({ data }) {
  if (!data) return null;
  const top = (data.top_5_mutants || []).slice(0, 6);
  // Radar data
  const radarData = (data.traits || []).map(t => {
    const row = { trait: t };
    top.forEach(m => { row[m.Mutant_ID] = m[t]; });
    return row;
  });
  return (
    <div className="space-y-4" data-testid="mutation-screening-view">
      <p className="text-sm bg-amber-50 rounded-lg p-3 border border-amber-200">{data.interpretation}</p>
      <div className="rounded-lg p-3 bg-amber-50 border border-amber-300 text-sm">
        <b>Formula:</b> <span className="font-mono">{data.formula}</span>
      </div>
      <Card title="Weights" testid="idx-weights">
        <div className="flex flex-wrap gap-3 text-sm">
          {Object.entries(data.weights).map(([k, v]) => (
            <div key={k} className="rounded px-3 py-1.5 bg-white border border-amber-200">
              {k}: <b className="font-mono">{v}</b>
            </div>
          ))}
          <div className="rounded px-3 py-1.5 bg-amber-100 border border-amber-300">
            Total weight: <b className="font-mono">{data.total_weight}</b>
          </div>
        </div>
      </Card>
      <Card title="Top 5 Mutants" testid="idx-top"><Table rows={data.top_5_mutants} /></Card>
      <Card title="Full Ranking" testid="idx-ranking"><Table rows={data.ranking} /></Card>
      <Card title="Radar — Top 6 Mutants" testid="idx-radar">
        <div style={{ width: '100%', height: 340 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#FCD34D" />
              <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis tick={{ fontSize: 10 }} />
              {top.map((m, i) => (
                <Radar key={m.Mutant_ID} name={`${m.Mutant_ID} (#${m.Rank})`} dataKey={m.Mutant_ID}
                  stroke={['#B45309', '#D97706', '#F59E0B', '#2563EB', '#059669', '#7C3AED'][i % 6]}
                  fill={['#B45309', '#D97706', '#F59E0B', '#2563EB', '#059669', '#7C3AED'][i % 6]} fillOpacity={0.15} />
              ))}
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Citations />
    </div>
  );
}
